<template>
  <!-- Prize logo -->
  <div
    v-if="mode === QuizFanCtaMode.DefaultPrize"
    clasds="flex flex-row items-center gap-4 w-full p-4 rounded-standard bg-surface backdrop-blur-[35px]"
    class="flex flex-row justify-center items-start gap-2 self-stretch w-full px-3 py-2.5"
  >
    <div
      class="flex items-center rounded-lg bg-brand-quaternary-40 overflow-hidden"
    >
      <img
        :src="quizCardState.quiz?.prize?.image || '/quiz/generic-reward.png'"
        :alt="$t('channel.contests.quiz.question.defaultPrize')"
        class="size-[34px] bg-lightgray bg-center bg-cover bg-no-repeat"
      />
    </div>
    <div class="flex justify-start items-center gap-[10px] flex-1 line-clamp-2">
      <p
        class="text-basics-secondary-80 font-sans text-xs font-medium leading-normal flex-1 line-clamp-2"
      >
        {{ quizCardState.quiz?.prize?.description }}
      </p>
    </div>
  </div>

  <!-- Fan CTA -->
  <div
    v-if="mode === QuizFanCtaMode.Respond"
    class="flex flex-col items-center gap-2 w-full px-3 py-3 rounded-standard bg-surface backdrop-blur-[35px] text-sm"
  >
    <div class="flex items-start gap-2 self-stretch">
      <ContestButton
        variant="primary"
        class="!h-8 !px-4 text-xs"
        :is-loading="voting"
        @click="onVote(quizCardState.selectedOptionId!)"
      >
        {{ $t("channel.contests.quiz.actions.respond") }}
      </ContestButton>

      <ContestButton
        variant="subtle"
        class="!h-8 !px-4 text-xs"
        :disabled="voting"
        @click="quizCardState.selectedOptionId = null"
      >
        {{ $t("channel.contests.quiz.actions.cancel") }}
      </ContestButton>
    </div>
  </div>

  <!-- Question navigation  -->
  <div
    v-if="mode === QuizFanCtaMode.Navigation"
    class="flex h-14 items-center gap-1.5 w-full px-3 py-1.5 rounded-standard bg-fill-brand-secondary backdrop-blur-[35px]"
  >
    <div
      class="flex h-11 min-w-0 flex-1 items-center gap-1.5 rounded-full px-2 text-white"
      role="status"
      :aria-label="navigationLabel"
      :title="navigationLabel"
    >
      <Icon
        :icon="navigationStatusIcon"
        class="size-5 shrink-0"
        :class="
          hasAnsweredCurrentQuestion
            ? 'text-fill-success'
            : 'text-brand-bgdivider-20'
        "
        aria-hidden="true"
      />
      <span class="truncate whitespace-nowrap text-sm font-medium leading-none">
        {{ navigationStatusLabel }}
      </span>
    </div>

    <div class="ml-auto flex shrink-0 items-center gap-1.5">
      <ContestButton
        variant="subtle"
        class="!size-11 !flex-none !px-0 !py-0"
        :disabled="quizCardState.currentQuestionNumber === 0"
        :aria-label="$t('channel.contests.quiz.actions.previousQuestion')"
        :title="$t('channel.contests.quiz.actions.previousQuestion')"
        @click="quizCardState.goToPreviousQuestion"
      >
        <Icon icon="mdi:chevron-left" class="size-5" aria-hidden="true" />
      </ContestButton>

      <ContestButton
        variant="subtle"
        class="!size-11 !flex-none !px-0 !py-0"
        :disabled="
          quizCardState.currentQuestionNumber === quizCardState.questionsCount
        "
        :aria-label="$t('channel.contests.quiz.actions.nextQuestion')"
        :title="$t('channel.contests.quiz.actions.nextQuestion')"
        @click="quizCardState.goToNextQuestion"
      >
        <Icon icon="mdi:chevron-right" class="size-5" aria-hidden="true" />
      </ContestButton>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useQuizCardState } from "../quiz-card/quiz-card-state";
import { QuestionStatus } from "@/generated/graphql";
import ContestButton from "../contest-button.vue";
import { useQuizVote } from "../quiz-card/use-quiz-vote";
import { useChannelStore } from "@/stores/channel.store";
import { useI18n } from "vue-i18n";
import { Icon } from "@iconify/vue";

const quizCardState = useQuizCardState();
const _channelStore = useChannelStore();
const { t } = useI18n();

const emit = defineEmits<{
  answerSubmitted: [];
}>();

enum QuizFanCtaMode {
  DefaultPrize = "default-prize",
  Navigation = "navigation",
  Respond = "respond",
}

const mode = computed(() => {
  if (quizCardState.selectedOptionId) {
    return QuizFanCtaMode.Respond;
  }

  if (
    (quizCardState.currentQuestion?.status === QuestionStatus.Done ||
      quizCardState.currentQuestion?.selected_option_id) &&
    !quizCardState.selectedOptionId
  ) {
    return QuizFanCtaMode.Navigation;
  }

  return QuizFanCtaMode.DefaultPrize;
});

const navigationLabel = computed(() => {
  return quizCardState.currentQuestion?.selected_option_id
    ? t("channel.contests.quiz.questionAnswered")
    : t("channel.contests.quiz.questionExpired");
});

const hasAnsweredCurrentQuestion = computed(() => {
  return Boolean(quizCardState.currentQuestion?.selected_option_id);
});

const navigationStatusLabel = computed(() => {
  return hasAnsweredCurrentQuestion.value
    ? t("channel.contests.quiz.questionAnsweredShort")
    : t("channel.contests.quiz.questionExpiredShort");
});

const navigationStatusIcon = computed(() => {
  return hasAnsweredCurrentQuestion.value
    ? "mdi:check-circle"
    : "mdi:clock-alert-outline";
});

const { submitAnswer: onVote, voting } = useQuizVote(() =>
  emit("answerSubmitted"),
);
</script>
